/**
 * Route table for the admin section.
 *
 * Mounted under `/admin` in `App.tsx`. Uses the router that wraps the whole
 * app. Each leaf route goes through the AdminGate — the gate is applied at
 * the layout level so pages only render once we've confirmed the caller
 * has the `admin` capability.
 */

import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminLayout } from './AdminLayout';
import { AdminGate } from './AdminGate';
import { AccessDeniedPage } from './AccessDeniedPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { GroupsListPage } from './pages/GroupsListPage';
import { GroupFormPage } from './pages/GroupFormPage';
import { UsersListPage } from './pages/UsersListPage';
import { UserFormPage } from './pages/UserFormPage';
import { BulkImportPage } from './pages/BulkImportPage';
import { AccountsCatalogPage } from './pages/AccountsCatalogPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { BugReportsPage } from './pages/BugReportsPage';
import { TemplatesListPage } from './pages/TemplatesListPage';
import { TemplateFormPage } from './pages/TemplateFormPage';
import { DepartmentsListPage } from './pages/DepartmentsListPage';
import { DepartmentFormPage } from './pages/DepartmentFormPage';

export const AdminRoutes: React.FC = () => {
  return (
    <AdminGate fallback={<AccessDeniedPage />}>
      <Routes>
        <Route element={<AdminLayout />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="groups" element={<GroupsListPage />} />
          <Route path="groups/new" element={<GroupFormPage />} />
          {/*
            Redirect the bare ``groups/:posix_group`` path to ``/edit``.
            Previously both paths rendered ``<GroupFormPage />`` directly,
            which was the only resource with a bare-param route and meant
            navigating to ``/admin/groups/some-group`` silently opened the
            edit form without any indication of intent. Users, templates,
            and departments all only expose the ``/edit`` variant, so
            redirecting here keeps routing consistent and gives the edit
            form a single canonical URL. Any links that still point at
            the bare path (e.g. dashboard quick links) flow through this
            redirect transparently.
          */}
          <Route
            path="groups/:posix_group"
            element={<Navigate to="edit" replace />}
          />
          <Route path="groups/:posix_group/edit" element={<GroupFormPage />} />
          <Route path="users" element={<UsersListPage />} />
          <Route path="users/new" element={<UserFormPage />} />
          <Route path="users/bulk-import" element={<BulkImportPage />} />
          <Route path="users/:user_alias/edit" element={<UserFormPage />} />
          <Route path="accounts" element={<AccountsCatalogPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="bug-reports" element={<BugReportsPage />} />
          <Route path="templates" element={<TemplatesListPage />} />
          <Route path="templates/new" element={<TemplateFormPage />} />
          <Route path="templates/:template_name/edit" element={<TemplateFormPage />} />
          <Route path="departments" element={<DepartmentsListPage />} />
          <Route path="departments/new" element={<DepartmentFormPage />} />
          <Route path="departments/:department_name/edit" element={<DepartmentFormPage />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>
    </AdminGate>
  );
};
